import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { numberWithCommas } from 'src/utils/numberFormat'
import { Option } from 'src/components/Select/interfaces/option.interface'
import {
  ActivityFrequencyData,
  ActivityFrequencyMockData,
} from 'src/pages/ContentAnalysisDetail/mockData/activityFrequencyMockData'
import { ReactComponent as GlobeIcon } from 'src/assets/svg/Globe.svg'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import { SOCIAL_ICONS } from 'src/constants/socialIcons'
import styles from './activityFrequencySection.module.scss'

type TabType = 'engagement' | 'posts'

interface ActivityFrequencySectionProps {
  engagementData: ActivityFrequencyMockData
  isLoading?: boolean
  platforms?: string[]
  postsData: ActivityFrequencyMockData
}

interface TooltipData {
  day: string
  dayIdx: number
  hour: string
  hourIdx: number
  value: number
  x: number
  y: number
}

const TABS = [
  { label: 'Activity Frequency of Posts', value: 'posts' as TabType },
  { label: 'Activity Frequency of Engagement', value: 'engagement' as TabType },
]

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

const buildPlatformOptions = (platforms?: string[]): Option[] => {
  const all: Option = { label: 'All Platform', value: 'allPlatform' }
  if (!platforms || platforms.length === 0) return [all]
  return [
    all,
    ...platforms.map((p) => ({
      label: platformDisplayLabel(p, capitalize(p)),
      value: p,
    })),
  ]
}

const getPlatformIcon = (value: string): ReactNode => {
  if (value === 'allPlatform') return <GlobeIcon />
  return SOCIAL_ICONS[value] || <GlobeIcon />
}

const getMaxValue = (data: number[][]): number => {
  if (!data || data.length === 0) return 1
  return Math.max(...data.map((item) => item[2]), 1)
}

const getOpacityForValue = (value: number, maxValue: number): number => {
  if (value === 0) return 0.15
  const ratio = value / maxValue
  if (ratio > 0.7) return 1
  if (ratio > 0.4) return 0.7
  if (ratio > 0.2) return 0.5
  return 0.3
}

export const ActivityFrequencySection: FC<ActivityFrequencySectionProps> = ({
  isLoading = false,
  platforms,
  postsData,
  engagementData,
}) => {
  const platformOptions = useMemo(
    () => buildPlatformOptions(platforms),
    [platforms]
  )
  const [activeTab, setActiveTab] = useState<TabType>('posts')
  const [selectedPlatform, setSelectedPlatform] = useState<Option>(
    platformOptions[0]
  )

  // Reset selected platform when platform options change
  useEffect(() => {
    setSelectedPlatform(platformOptions[0])
  }, [platformOptions])
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [tooltip, setTooltip] = useState<TooltipData | null>(null)
  const [isPinned, setIsPinned] = useState(false)
  const [isTooltipDropdownOpen, setIsTooltipDropdownOpen] = useState(false)
  const [tooltipPlatform, setTooltipPlatform] = useState<Option | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const tooltipDropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false)
      }
      if (
        tooltipDropdownRef.current &&
        !tooltipDropdownRef.current.contains(event.target as Node)
      ) {
        setIsTooltipDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const currentData: ActivityFrequencyData = useMemo(() => {
    const dataSource = activeTab === 'posts' ? postsData : engagementData
    const raw = dataSource[selectedPlatform.value] || dataSource.allPlatform

    // Normalize hour format: "8:00" -> "08:00"
    const padHour = (h: string) => {
      const [hr, min] = h.split(':')
      return `${hr.padStart(2, '0')}:${min}`
    }
    const normalizedYAxis = raw.yAxis.map(padHour)
    // Generate consistent 3-hour interval labels from yAxis
    const visibleHours = normalizedYAxis.filter((h) => {
      const hr = parseInt(h.split(':')[0], 10)
      return hr % 3 === 0
    })

    // Normalize yAxis to start from 00:00
    const parseHour = (h: string) => parseInt(h.split(':')[0], 10)
    const sorted = [...normalizedYAxis].sort(
      (a, b) => parseHour(a) - parseHour(b)
    )
    const isAlreadySorted = sorted.every((h, i) => h === normalizedYAxis[i])
    if (isAlreadySorted) {
      return { ...raw, visibleHours, yAxis: normalizedYAxis }
    }

    // Build index mapping: new position -> old position
    const oldIndexOf = sorted.map((h) => normalizedYAxis.indexOf(h))
    const newData = raw.data.map(([dayIdx, hourIdx, value]) => [
      dayIdx,
      oldIndexOf.indexOf(hourIdx),
      value,
    ])

    return { ...raw, data: newData, visibleHours, yAxis: sorted }
  }, [activeTab, selectedPlatform, postsData, engagementData])

  const maxValue = useMemo(() => getMaxValue(currentData.data), [currentData])

  const gridData = useMemo(() => {
    const grid: { [key: string]: number } = {}
    currentData.data.forEach(([dayIdx, hourIdx, value]) => {
      grid[`${dayIdx}-${hourIdx}`] = value
    })
    return grid
  }, [currentData])

  const handleCellHover = (
    e: React.MouseEvent,
    dayIdx: number,
    hourIdx: number,
    value: number
  ) => {
    if (isPinned) return // Don't update tooltip if it's pinned

    const rect = e.currentTarget.getBoundingClientRect()
    const containerRect = e.currentTarget
      .closest(`.${styles.chartGrid}`)
      ?.getBoundingClientRect()
    if (!containerRect) return

    setTooltip({
      day: currentData.xAxis[dayIdx],
      dayIdx,
      hour: currentData.yAxis[hourIdx],
      hourIdx,
      value,
      x: rect.left - containerRect.left + rect.width / 2,
      y: rect.top - containerRect.top,
    })
  }

  const handleCellLeave = () => {
    if (!isPinned) {
      setTooltip(null)
    }
  }

  const handleCellClick = (
    e: React.MouseEvent,
    dayIdx: number,
    hourIdx: number,
    value: number
  ) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const containerRect = e.currentTarget
      .closest(`.${styles.chartGrid}`)
      ?.getBoundingClientRect()
    if (!containerRect) return

    setTooltip({
      day: currentData.xAxis[dayIdx],
      dayIdx,
      hour: currentData.yAxis[hourIdx],
      hourIdx,
      value,
      x: rect.left - containerRect.left + rect.width / 2,
      y: rect.top - containerRect.top,
    })
    setIsPinned(true)
    setTooltipPlatform(null)
    setIsTooltipDropdownOpen(false)
  }

  const handleTooltipClose = () => {
    setTooltip(null)
    setIsPinned(false)
    setTooltipPlatform(null)
    setIsTooltipDropdownOpen(false)
  }

  const handlePlatformSelect = (option: Option) => {
    setSelectedPlatform(option)
    setIsDropdownOpen(false)
  }

  const handleTooltipPlatformSelect = (option: Option) => {
    setTooltipPlatform(option)
    setIsTooltipDropdownOpen(false)
  }

  // Get cell value for a specific platform at the tooltip's position
  const getCellValueForPlatform = (platformValue: string): number => {
    if (!tooltip) return 0
    const dataSource = activeTab === 'posts' ? postsData : engagementData
    const platformData = dataSource[platformValue] || dataSource.allPlatform
    const grid: { [k: string]: number } = {}
    platformData.data.forEach(([d, h, v]) => {
      grid[`${d}-${h}`] = v
    })
    return grid[`${tooltip.dayIdx}-${tooltip.hourIdx}`] || 0
  }

  // Get tooltip values for the selected tooltip platform
  const getTooltipValues = () => {
    if (!tooltip) return { avg: 0, val: 0 }
    const platform = tooltipPlatform || selectedPlatform
    const dataSource = activeTab === 'posts' ? postsData : engagementData
    const platformData = dataSource[platform.value] || dataSource.allPlatform
    const val = getCellValueForPlatform(platform.value)
    const avg = platformData.averages?.[tooltip.hour] ?? 0
    return { avg, val }
  }

  const tooltipValues = getTooltipValues()
  const activeTooltipPlatform = tooltipPlatform || selectedPlatform

  return (
    <div className={styles.activityFrequencySection}>
      <div className={styles.header}>
        <div className={styles.tabs}>
          {TABS.map((tab) => (
            <button
              className={classNames(styles.tab, {
                [styles.active]: tab.value === activeTab,
              })}
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className={styles.dropdownWrapper} ref={dropdownRef}>
          <button
            className={styles.platformDropdown}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            type="button"
          >
            <span className={styles.platformIcon}>
              {getPlatformIcon(selectedPlatform.value)}
            </span>
            <span>{selectedPlatform.label}</span>
            <span className={styles.chevron}>▾</span>
          </button>

          {isDropdownOpen && (
            <div className={styles.dropdownMenu}>
              {platformOptions.map((option) => (
                <button
                  className={classNames(styles.dropdownItem, {
                    [styles.selected]: option.value === selectedPlatform.value,
                  })}
                  key={option.value}
                  onClick={() => handlePlatformSelect(option)}
                  type="button"
                >
                  <span className={styles.dropdownIcon}>
                    {getPlatformIcon(option.value)}
                  </span>
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={styles.chartContainer}>
        <div className={styles.chartGrid}>
          {/* Header row with day labels */}
          <div className={styles.headerRow}>
            <div className={styles.hourLabel} />
            {currentData.xAxis.map((day) => (
              <div className={styles.dayLabel} key={day}>
                {day}
              </div>
            ))}
          </div>

          {/* Data rows */}
          {currentData.yAxis.map((hour, hourIdx) => {
            const showLabel = currentData.visibleHours.includes(hour)
            return (
              <div className={styles.dataRow} key={hour}>
                {/* The axes are structure, not measurement (always 7 days x
                    24 hours), so the real labels stay while loading. */}
                <div className={styles.hourLabel}>{showLabel && hour}</div>
                {currentData.xAxis.map((day, dayIdx) => {
                  // While loading the cells carry no measurement, so they are
                  // not hoverable — a tooltip here would report a fake 0. They
                  // pulse at the empty-cell opacity: a bright skeleton on the
                  // purple card read as a grid full of maximum values.
                  if (isLoading) {
                    return (
                      <div className={styles.cell} key={`${day}-${hour}`}>
                        <div
                          className={classNames(styles.bar, styles.barLoading)}
                        />
                      </div>
                    )
                  }

                  const value = gridData[`${dayIdx}-${hourIdx}`] || 0
                  const opacity = getOpacityForValue(value, maxValue)
                  return (
                    <div
                      className={styles.cell}
                      key={`${day}-${hour}`}
                      onClick={(e) =>
                        handleCellClick(e, dayIdx, hourIdx, value)
                      }
                      onMouseEnter={(e) =>
                        handleCellHover(e, dayIdx, hourIdx, value)
                      }
                      onMouseLeave={handleCellLeave}
                    >
                      <div className={styles.bar} style={{ opacity }} />
                    </div>
                  )
                })}
              </div>
            )
          })}

          {/* Tooltip */}
          {tooltip && (
            <div
              className={styles.tooltip}
              style={{
                left: tooltip.x,
                top: tooltip.y,
              }}
            >
              {isPinned && (
                <button
                  className={styles.tooltipClose}
                  onClick={handleTooltipClose}
                  type="button"
                >
                  ×
                </button>
              )}
              <div className={styles.tooltipTitle}>
                {tooltip.day} at {tooltip.hour}
              </div>
              <div
                className={styles.tooltipDropdownWrapper}
                ref={tooltipDropdownRef}
              >
                <button
                  className={styles.tooltipPlatform}
                  onClick={() =>
                    setIsTooltipDropdownOpen(!isTooltipDropdownOpen)
                  }
                  type="button"
                >
                  <span className={styles.tooltipPlatformIcon}>
                    {getPlatformIcon(activeTooltipPlatform.value)}
                  </span>
                  <span>{activeTooltipPlatform.label}</span>
                  <span className={styles.tooltipChevron}>▾</span>
                </button>
                {isTooltipDropdownOpen && (
                  <div className={styles.tooltipDropdownMenu}>
                    {platformOptions.map((option) => (
                      <button
                        className={classNames(styles.tooltipDropdownItem, {
                          [styles.selected]:
                            option.value === activeTooltipPlatform.value,
                        })}
                        key={option.value}
                        onClick={() => handleTooltipPlatformSelect(option)}
                        type="button"
                      >
                        <span className={styles.dropdownIcon}>
                          {getPlatformIcon(option.value)}
                        </span>
                        <span className={styles.dropdownItemLabel}>
                          {option.label}
                        </span>
                        <span className={styles.dropdownItemValue}>
                          {numberWithCommas(
                            getCellValueForPlatform(option.value)
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className={styles.tooltipRow}>
                <span>
                  Total {activeTab === 'posts' ? 'Posts Created' : 'Engagement'}
                </span>
                <span className={styles.tooltipValue}>
                  {numberWithCommas(tooltipValues.val)}
                </span>
              </div>
              <div className={styles.tooltipRow}>
                <span>Average</span>
                <span className={styles.tooltipValue}>
                  {numberWithCommas(tooltipValues.avg)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
